import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Papa from 'papaparse';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { PageContainer } from 'layouts';
import { ProductsContext } from 'contexts';
import formsConfig from '../ProductForm/formsConfig';

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 200,
  },
  uploadSection: {
    margin: theme.spacing(2, 0),
  },
  mappingTable: {
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 20),
  },
  progress: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(20),
  },
}));

const labels = {
  "blh": "BLHN",
  "vpgt": "FS"
};

const defaultMapping = {
  partnumber: 'sku',
  product_type: 'specifications.type',
  lc_type: 'specifications.loadcell_type',
  model_name: 'name',
  long_product_name: 'long_name',
  brand: 'specifications.brand',
  max_nr_intervals: 'specifications.maxiuntervals',
  rated_output: 'specifications.ratedoutput',
  digital_analog: 'specifications.productnature',
  // y_emax_vmin: 'specifications.emaxvmin',
  communication_output: 'specifications.communications_output',
  certificates_approvals: 'specifications.certificates',
  accuracy_class: 'specifications.accuracy_class',
  enclosure_rating: 'specifications.environment_protection_class',
  material: 'specifications.material',
  mounting: 'specifications.mounting_type',
  nominal_capacity_value: 'specifications.nominal_capacity_value',
  nominal_capacity_unit: 'specifications.nominal_capacity_unit',
  power_supply: 'specifications.powersupply',
  display_size: 'specifications.display',
  communication_protocols: 'specifications.communication_protocols',
  channels: 'specifications.weighing_channels_single_multiple',
  related_products: 'related_products',
  description: 'description',
  keywords: 'keywords',
  weight: 'specifications.weight',
  options: 'optional',
  industries: 'specifications.industry',
  applications: 'specifications.application',
  markets: 'specifications.market_applications',
  regions: 'specifications.regions',
  features: 'features.features',
  image: 'images[0]',
  datasheet: 'res_id',
  document: 'doc_number',
};


const ProductsImport = () => {
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { products },
    actions: productsActions,
  } = useContext(ProductsContext);

  const [bu, setBu] = useState('');
  const [category, setCategory] = useState('');
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [productFields, setProductFields] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setCsvHeaders(results.meta.fields);
          setCsvData(results.data);
        },
      });
    }
  };

  const handleBuChange = (event) => {
    const newBu = event.target.value;
    setBu(newBu);
    setCategory('');
    setProductFields([]);
    setMapping({});
  };

  const handleCategoryChange = (event) => {
    const newCategory = event.target.value;
    setCategory(newCategory);
    if (bu && newCategory && formsConfig[bu]?.[newCategory]) {
      const config = formsConfig[bu][newCategory];
      const fields = config.formFields.map((f) => {
        return {
          label: f.props.label,
          value: f.props.name
        }
      });
      // Add other potential root fields from initialValues
      const allFields = [
        ...new Set([...fields]), // , ...Object.keys(config.initialValues)
      ];
      setProductFields(allFields);
    } else {
      setProductFields([]);
    }
    setMapping({});
  };

  const handleMappingChange = (csvHeader, productField) => {
    setMapping((prev) => ({ ...prev, [csvHeader]: productField }));
  };

  const successNotification = (message) =>
    enqueueSnackbar(message, {
      variant: 'success',
      anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    });

  const errorNotification = (message) =>
    enqueueSnackbar(message, {
      variant: 'error',
      anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    });

  const handleImport = async () => {
    if (!bu || !category) {
      errorNotification('Please select a Business Unit and Category.');
      return;
    }

    setIsImporting(true);
    setImportProgress(0);

    let index = 0;
    for (const row of csvData) {
      const formConfig = formsConfig[bu][category];
      const productData = { ...formConfig.initialValues };
      productData.isImport = true;

      for (const csvHeader in mapping) {
        const productField = mapping[csvHeader];
        if (productField && row[csvHeader] !== undefined) {
          // This is a simplified assignment. You might need to handle nested objects
          // and arrays (e.g., 'specifications.some_spec') more robustly.
          const field = formConfig.formFields.find(el => el.props.name === productField);
          const keys = productField.split('.');
          let current = productData;
          for (let i = 0; i < keys.length - 1; i++) {
            current[keys[i]] = current[keys[i]] || {};
            current = current[keys[i]];
          }
          if(field && field.props.multiple){
            current[keys[keys.length - 1]] = row[csvHeader].split('|').map(el=> el.trim());
          } else {
            current[keys[keys.length - 1]] = row[csvHeader];
          }
        }
      }

      let existingProduct = null;
      // Assuming 'name' is the unique identifier for update.
      if(productData.sku){
        existingProduct = products.find(
          (p) => p.sku === productData.sku && p.business_unit === bu && p.category === category,
        );
      } else if(productData.name){
        existingProduct = products.find(
          (p) => p.name === productData.name && p.business_unit === bu && p.category === category,
        );
      }


      if (existingProduct) {
        await productsActions.updateProduct(
          existingProduct._id,
          bu,
          category,
          { ...existingProduct, ...productData },
          () => successNotification(`Product "${productData.name}" updated.`),
        );
      } else {
        await productsActions.createProduct(bu, category, productData, () =>
          successNotification(`Product "${productData.name}" created.`)
        ).catch(err => {
          console.log('error message', err)
        });
      }
      index++;
      setImportProgress((index / csvData.length) * 100);
    }
    productsActions.getProducts();
    setIsImporting(false);
    history.push(`/products/${bu}/${category}`);
  };

  return (
    <PageContainer component="main" maxWidth="lg">
      <Container>
        <Typography variant="h4" gutterBottom>
          Import Products
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <FormControl className={classes.formControl} fullWidth>
              <InputLabel>Business Unit</InputLabel>
              <Select value={bu} onChange={handleBuChange}>
                {Object.keys(formsConfig).map((key) => (
                  <MenuItem key={key} value={key}>
                    {labels[key]??key.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl className={classes.formControl} fullWidth disabled={!bu}>
              <InputLabel>Category</InputLabel>
              <Select value={category} onChange={handleCategoryChange}>
                {bu &&
                  Object.keys(formsConfig[bu])
                    .filter((key) => typeof formsConfig[bu][key] === 'object')
                    .map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} className={classes.uploadSection}>
            <Button variant="contained" component="label" disabled={!bu || !category}>
              Upload CSV
              <input type="file" accept=".csv" hidden onChange={handleFileChange} />
            </Button>
          </Grid>
        </Grid>

        {csvHeaders.length > 0 && (
          <div className={classes.mappingTable}>
            <Typography variant="h6">Map CSV Columns to Product Fields</Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>CSV Column</TableCell>
                  <TableCell>Product Field</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csvHeaders.map((header) => {
                  if(!mapping[header]){
                    const defaultValue = productFields.find(el => el.value === header || el.label === header || el.value === defaultMapping[header]);
                    if(defaultValue){
                      handleMappingChange(header, defaultValue.value)
                    }
                  }
                  return <TableRow key={header}>
                    <TableCell>{header}</TableCell>
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={mapping[header] || ''}
                          onChange={(e) => handleMappingChange(header, e.target.value)}
                        >
                          <MenuItem value="">
                            <em>Do not import</em>
                          </MenuItem>
                          {productFields.map((field) => (
                            <MenuItem key={field.value} value={field.value}>
                              {field.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                })}
              </TableBody>
            </Table>
            {isImporting ? (
              <Box display="flex" alignItems="center" className={classes.progress}>
                <Box width="100%" mr={1}>
                  <LinearProgress variant="determinate" value={importProgress} />
                </Box>
                <Box minWidth={35}>
                  <Typography variant="body2" color="textSecondary">{`${Math.round(
                    importProgress,
                  )}%`}</Typography>
                </Box>
              </Box>
            ) : (
              <Button
                type="button"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                onClick={handleImport}
                disabled={isImporting}
              >
                Start Import
              </Button>
            )}
          </div>
        )}
      </Container>
    </PageContainer>
  );
};

export default ProductsImport;
